import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Home, ArrowRight, MapPin, Check } from "lucide-react";

export default function Onboarding() {
  const { user, setUser, loading } = useAuth();
  const navigate = useNavigate();
  const [localities, setLocalities] = useState({ "Pune West": [], "Pune East": [] });
  const [zone, setZone] = useState("");
  const [locality, setLocality] = useState("");
  const [extras, setExtras] = useState([]);   // additional_localities
  const [rera, setRera] = useState("");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/localities").then(({ data }) => setLocalities(data)); }, []);
  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
    if (user?.onboarded) navigate("/dashboard", { replace: true });
  }, [loading, user, navigate]);

  // When primary changes, drop it from extras to avoid the API rejecting duplicates
  useEffect(() => {
    if (locality) setExtras((cur) => cur.filter((l) => l !== locality));
  }, [locality]);

  // All localities across both zones, with their zone label — for the multi-select pills
  const allLocalitiesByZone = useMemo(() => {
    return [
      { zone: "Pune West", items: localities["Pune West"] || [] },
      { zone: "Pune East", items: localities["Pune East"] || [] },
    ];
  }, [localities]);

  const toggleExtra = (loc) => {
    if (loc === locality) return; // primary cannot also be extra
    setExtras((cur) => (cur.includes(loc) ? cur.filter((x) => x !== loc) : [...cur, loc]));
  };

  const submit = async () => {
    if (!zone || !locality) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/onboarding", {
        maharera_number: rera || undefined,
        micro_market: zone,
        primary_locality: locality,
        additional_localities: extras,
        phone,
        bio,
      });
      setUser(data);
      toast.success("You're set. Welcome aboard.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your profile.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-sg-paper">
      <header className="bg-sg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full border border-sg-gold/40 flex items-center justify-center"><Home className="w-4 h-4 text-sg-gold" /></div>
            <span className="font-display text-lg italic">INPropPortal<span className="text-sg-gold">.com</span></span>
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="overline text-sg-gold-deep mb-3">STEP 1 of 1 · POST-SUBSCRIPTION SETUP</div>
        <h1 className="font-display text-4xl lg:text-5xl text-sg-navy">
          Tell us where you <span className="font-serif-italic text-sg-gold-deep">operate.</span>
        </h1>
        <p className="mt-3 text-sg-mute">
          Your dashboard will only surface Master Projects from your declared micro-market.
        </p>

        <div className="mt-10 bg-white border border-sg-line rounded-3xl p-8 space-y-6">
          <div className="space-y-2">
            <Label className="overline text-sg-gold-deep">MahaCRITI Reg No <span className="normal-case tracking-normal text-sg-mute font-normal">(optional)</span></Label>
            <Input
              data-testid="onboarding-rera-input"
              placeholder="A52100012345"
              value={rera}
              onChange={(e) => setRera(e.target.value.toUpperCase())}
              className="font-mono rounded-full border-sg-line"
            />
            <p className="text-xs text-sg-mute">Format: 1 letter + 8–13 digits (e.g., A52100012345). Leave blank if not yet registered — you can add it later from Branding.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="overline text-sg-gold-deep">Primary Corridor *</Label>
              <Select value={zone} onValueChange={(v) => { setZone(v); setLocality(""); }}>
                <SelectTrigger className="rounded-full border-sg-line" data-testid="onboarding-zone-select"><SelectValue placeholder="Select zone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pune West">Pune West</SelectItem>
                  <SelectItem value="Pune East">Pune East</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="overline text-sg-gold-deep">Primary Locality *</Label>
              <Select value={locality} onValueChange={setLocality} disabled={!zone}>
                <SelectTrigger className="rounded-full border-sg-line" data-testid="onboarding-locality-select"><SelectValue placeholder={zone ? "Select locality" : "Pick zone first"} /></SelectTrigger>
                <SelectContent>
                  {(localities[zone] || []).map((l) => (<SelectItem key={l} value={l}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3" data-testid="onboarding-additional-localities">
            <div className="flex items-baseline justify-between">
              <Label className="overline text-sg-gold-deep">Also Cover (optional)</Label>
              <span className="text-[11px] text-sg-mute">
                Tap pills for any extra micro-markets you actively transact in.
                {extras.length > 0 && (
                  <span className="ml-2 text-sg-navy font-semibold" data-testid="onboarding-extras-counter">
                    {extras.length} selected
                  </span>
                )}
              </span>
            </div>
            <div className="space-y-3">
              {allLocalitiesByZone.map(({ zone: z, items }) => (
                <div key={z}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-sg-mute/80 mb-1.5">{z}</div>
                  <div className="flex flex-wrap gap-2">
                    {items.map((loc) => {
                      const isPrimary = loc === locality;
                      const isExtra = extras.includes(loc);
                      const active = isPrimary || isExtra;
                      return (
                        <button
                          key={loc}
                          type="button"
                          onClick={() => toggleExtra(loc)}
                          disabled={isPrimary}
                          aria-pressed={active}
                          data-testid={`onboarding-extra-pill-${loc.toLowerCase().replace(/\s+/g, "-")}`}
                          className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1 transition-colors ${
                            isPrimary
                              ? "bg-sg-gold/20 text-sg-gold-deep border-sg-gold/50 cursor-not-allowed"
                              : isExtra
                              ? "bg-sg-navy text-white border-sg-navy"
                              : "bg-white text-sg-navy border-sg-line hover:bg-sg-cream-soft"
                          }`}
                        >
                          {isPrimary ? <MapPin className="w-3 h-3" /> : isExtra ? <Check className="w-3 h-3" /> : <MapPin className="w-3 h-3 opacity-60" />}
                          {loc}
                          {isPrimary && <span className="ml-1 text-[9px] opacity-80">PRIMARY</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {!locality && (
              <p className="text-[11px] text-sg-mute">Select a primary locality first; the chosen primary auto-locks in this list.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="overline text-sg-gold-deep">Phone (optional)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91 98••• ••456" className="rounded-full border-sg-line" />
          </div>

          <div className="space-y-2">
            <Label className="overline text-sg-gold-deep">Short Bio (optional)</Label>
            <Textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="12 years closing deals in West Pune. Specialise in premium 3 & 4 BHKs."
              rows={3}
              className="rounded-2xl border-sg-line"
            />
          </div>

          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full h-12 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white"
            data-testid="onboarding-submit"
          >
            {submitting ? "Saving…" : <>Complete Setup → Open Dashboard <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
